import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import SelectProperty from '../../Views/App-Views/RIC/webparts/ric/components/RIC/components/Search/SelectPropertys';
import Headers from '../../redux/Actions/Headers/Header';
import styles from './AppHeader.module.scss';

/**
 * "Rich" page header: icon + title/subtitle on the left, an optional
 * property picker on the right. Used by MainLayOut.js whenever a page sets
 * an `icon` via Headers.setHeader — fully generic, not tied to any module.
 */
const AppHeader = ({ icon, title, subtitle, actions }) => {
  const dispatch = useDispatch();
  const headerRef = useRef(null);
  const mainRef = useRef(null);
  const propertyRef = useRef(null);

  const descriptionBlock = subtitle ? (
    <div className={styles.subtitle}>{subtitle}</div>
  ) : null;

  const iconTitleBlock = (
    <div className={styles.main} ref={mainRef}>
      <div className={styles.icon}>{icon}</div>
      <div className={styles.text}>
        <div className={styles.title}>{title}</div>
        {descriptionBlock}
      </div>
    </div>
  );

  const hasPropertyAction = actions?.some((action) => action.type === "property");

  const propertyPickerBlock = hasPropertyAction ? (
    <div className={styles.property} ref={propertyRef}>
      {actions.map((action, index) => {
        if (action.type === "property") {
          return (
            <SelectProperty
              key={index}
              onChange={action?.onClick}
              style={{ width: '100%' }}
            />
          );
        }
        return null;
      })}
    </div>
  ) : null;

  useEffect(() => {
    if (!hasPropertyAction) {
      dispatch(Headers.setHeader({ isHeaderStacked: false }));
      return;
    }

    const headerEl = headerRef.current;
    const mainEl = mainRef.current;
    const propertyEl = propertyRef.current;
    if (!headerEl || !mainEl || !propertyEl) return;

    let lastStacked = null;
    const checkStacked = () => {
      const mainRect = mainEl.getBoundingClientRect();
      const propertyRect = propertyEl.getBoundingClientRect();
      const sameRow = mainRect.top < propertyRect.bottom && propertyRect.top < mainRect.bottom;
      const stacked = !sameRow;
      if (stacked !== lastStacked) {
        lastStacked = stacked;
        dispatch(Headers.setHeader({ isHeaderStacked: stacked }));
      }
    };

    checkStacked();
    const observer = new ResizeObserver(checkStacked);
    observer.observe(headerEl);
    return () => observer.disconnect();
  }, [dispatch, hasPropertyAction]);

  return (
    <div className={styles.header} ref={headerRef}>
      {iconTitleBlock}
      {propertyPickerBlock}
    </div>
  );
};

AppHeader.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  actions: PropTypes.arrayOf(PropTypes.object),
};

export default AppHeader;
